// THE GROUND LAW — how dark a ground is, as netti's shader reads it. No DOM, no
// GL: the engine, the browser bakers and the server compositors all import this,
// so "how netti looks on this ground" has one answer everywhere.
//
// netti is not one picture laid over a ground. The shader paints two different
// creatures and slides between them on `uDark`:
//   · PAPER (uDark 0, pale grounds) — transmitted watercolour. Pigment is the
//     colour, alpha is high, detail darkens toward colour.
//   · GLASS (uDark 1, dark grounds) — stained glass lit from within. Colour is
//     mostly emitted light, alpha drops to ~0.6 and the ground shows through.
// A transparent PNG freezes whichever creature was on screen when it was taken.
// Take one on a dark ground and lay it on eggshell and you get light on white
// through a thinned alpha: the washed-out netti. Take it on eggshell and lay it
// on a jewel field and the pigment goes flat and muddy.
//
// So a baked netti travels as a PAIR — the paper end and the glass end, rendered
// from the same frame with the same crop — and whoever composites it onto a
// ground blends the pair by that ground's darkness (`blendForGround` in
// lib/brand/groundTrue.ts). The picture then wears the look the live engine
// would have painted on that very ground.
//
// PRESENCE (FutureOrganism.setPresence, 2026-09-30) is the one exception to
// "two creatures": at presence 1 the live engine keeps the PAPER body on any
// ground (washes, pooled veins, alpha), lets the ground's hue into its thin
// washes, and makes the node cells and veins glow by the ground's darkness.
// Since 2026-10-02 it is the ENGINE DEFAULT (law_netti_presence): every live
// netti wears it, on every surface. It is live-only: a baked sheet is still
// the plain law until the sheets are re-baked with presence.

export type RGB01 = [number, number, number];

/** Rec.601 luma, 0..1 — the weighting the shader has always used. */
export function lumaOf(c: RGB01): number {
  return 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2];
}

/**
 * 0 (paper) … 1 (glass). EXACTLY the shader's `uDark` uniform: fully paper at
 * luma ≥ 0.62, fully glass at luma ≤ 0.22, linear between.
 */
export function groundDarkness(ground: RGB01): number {
  const v = 1 - (lumaOf(ground) - 0.22) / 0.4;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** the two ends of the law */
export const PAPER = 0;
export const GLASS = 1;

/**
 * A GROUND SHEET — how a baked netti travels. The same frame painted at these
 * five darknesses, stacked top→bottom in ONE png, every frame the same size and
 * crop. Two ends alone are exact only at the ends (the shader is not linear in
 * darkness: ~20–26/255 off on the jewel grounds between); five stops read
 * piecewise-quadratically land within ~3/255 of the live engine on every ground
 * we ship onto. Measured: /dev/netti-grounds + scripts/netti-ground-bench.mjs.
 */
export const SHEET_STOPS = [0, 0.25, 0.5, 0.75, 1] as const;

/** `#rrggbb` / `rrggbb` / `rgb(r,g,b)` / `hsl(h,s%,l%)` → 0..1 RGB, or null. */
export function parseGround(css: string | null | undefined): RGB01 | null {
  if (!css) return null;
  const s = css.trim();
  const hex = s.replace(/^#/, "");
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as RGB01;
  }
  const rgb = s.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
  if (rgb) return [+rgb[1] / 255, +rgb[2] / 255, +rgb[3] / 255];
  const hsl = s.match(/^hsla?\(\s*([\d.]+)(?:deg)?[\s,]+([\d.]+)%[\s,]+([\d.]+)%/i);
  if (hsl) {
    const h = +hsl[1];
    const sat = +hsl[2] / 100;
    const l = +hsl[3] / 100;
    const a = sat * Math.min(l, 1 - l);
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      return l - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)));
    };
    return [f(0), f(8), f(4)];
  }
  return null;
}
